/**
 * Strategy List Page
 *
 * Displays all strategies with filtering, quick actions, and a create modal.
 * Row-level actions: edit name/description/horizon, activate, archive, delete.
 * Full details available via the detail page.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ArchiveBoxIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { StrategyConnectivityHelpButton } from './StrategyConnectivityDocModal';
import { EntityListPage } from '../../../components/collection';
import type { FacetDefinition, SortOption, TableColumnWithRender } from '../../../components/collection/types';
import type { Strategy, StrategyTimeHorizon } from '../../../types/prioritization/strategy';
import {
  getStrategyStatusLabel,
  getTimeHorizonLabel,
} from '../../../types/prioritization/strategy';

// ── Row action menu ────────────────────────────────────────────────────────────

interface RowActionsProps {
  strategy: Strategy;
  onEdit: (s: Strategy) => void;
  onDelete: (s: Strategy) => void;
  onActivate: (s: Strategy) => void;
  onArchive: (s: Strategy) => void;
}

function RowActions({ strategy, onEdit, onDelete, onActivate, onArchive }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
        title="Actions"
      >
        <EllipsisVerticalIcon className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-[var(--bg-raised)] border border-[var(--rule)] shadow-lg z-50 py-1">
          <button
            onClick={() => { onEdit(strategy); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            <PencilIcon className="h-3.5 w-3.5 text-[var(--text-ter)]" /> Edit
          </button>
          <Link
            to={`/prioritization/strategy/${strategy.id}`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-[var(--text-ter)]" /> Open detail
          </Link>
          <div className="border-t border-[var(--rule)] my-1" />
          {strategy.status === 'draft' && (
            <button
              onClick={() => { onActivate(strategy); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]"
            >
              <CheckCircleIcon className="h-3.5 w-3.5" /> Activate
            </button>
          )}
          {strategy.status === 'active' && (
            <button
              onClick={() => { onArchive(strategy); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--mustard)] hover:bg-[var(--mustard-dim)]"
            >
              <ArchiveBoxIcon className="h-3.5 w-3.5" /> Archive
            </button>
          )}
          {strategy.status === 'archived' && (
            <button
              onClick={() => { onActivate(strategy); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]"
            >
              <CheckCircleIcon className="h-3.5 w-3.5" /> Re-activate
            </button>
          )}
          <div className="border-t border-[var(--rule)] my-1" />
          <button
            onClick={() => { onDelete(strategy); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
          >
            <TrashIcon className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────────

function StrategyStatusBadge({ status }: { status: Strategy['status'] }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        status === 'active'
          ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
          : status === 'draft'
            ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
            : 'bg-[var(--coral-dim)] text-[var(--coral)]'
      }`}
    >
      {getStrategyStatusLabel(status)}
    </span>
  );
}

// ── Quick-create / edit modal ──────────────────────────────────────────────────

interface StrategyFormModalProps {
  mode: 'create' | 'edit';
  initial?: { name: string; description: string; timeHorizon: StrategyTimeHorizon };
  onSave: (values: { name: string; description: string; timeHorizon: StrategyTimeHorizon }) => void;
  onClose: () => void;
}

function StrategyFormModal({ mode, initial, onSave, onClose }: StrategyFormModalProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [timeHorizon, setTimeHorizon] = useState<StrategyTimeHorizon>(initial?.timeHorizon ?? 'annual');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-[var(--surface)] bg-opacity-75" onClick={onClose} />
        <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">
              {mode === 'create' ? 'New Strategy' : 'Edit Strategy'}
            </h3>
            <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Name <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Q3 2026 Product Strategy"
                autoFocus
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm bg-[var(--bg-raised)] text-[var(--text-pri)]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this strategy for?"
                rows={2}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm resize-none bg-[var(--bg-raised)] text-[var(--text-pri)]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Time Horizon
              </label>
              <select
                value={timeHorizon}
                onChange={(e) => setTimeHorizon(e.target.value as StrategyTimeHorizon)}
                className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-indigo-500 focus:border-[var(--slate)] text-sm bg-[var(--bg-raised)] text-[var(--text-pri)]"
              >
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
                <option value="multi-year">Multi-Year</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            {mode === 'create' && (
              <Link
                to="/prioritization/strategy/new"
                onClick={onClose}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] flex items-center gap-1"
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                Advanced create (choose framework)
              </Link>
            )}
            {mode === 'edit' && <span />}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
              >
                Cancel
              </button>
              <button
                onClick={() => onSave({ name: name.trim(), description: description.trim(), timeHorizon })}
                disabled={!name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Facets & sort ──────────────────────────────────────────────────────────────

const FACETS: FacetDefinition<string>[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'multi',
    options: [
      { value: 'active',   label: 'Active' },
      { value: 'draft',    label: 'Draft' },
      { value: 'archived', label: 'Archived' },
    ],
  },
  {
    field: 'timeHorizon',
    label: 'Horizon',
    type: 'multi',
    options: [
      { value: 'quarterly',  label: 'Quarterly' },
      { value: 'annual',     label: 'Annual' },
      { value: 'multi-year', label: 'Multi-Year' },
    ],
  },
];

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'name',      label: 'Name' },
  { field: 'status',    label: 'Status' },
  { field: 'updatedAt', label: 'Last Updated' },
  { field: 'createdAt', label: 'Created' },
];

// ── Page ───────────────────────────────────────────────────────────────────────

export function StrategyList() {
  const navigate = useNavigate();
  const strategiesMap  = useStrategyStore(state => state.strategies);
  const createStrategy = useStrategyStore(state => state.createStrategy);
  const updateStrategy = useStrategyStore(state => state.updateStrategy);
  const deleteStrategy = useStrategyStore(state => state.deleteStrategy);
  const activateStrategy = useStrategyStore(state => state.activateStrategy);
  const archiveStrategy  = useStrategyStore(state => state.archiveStrategy);
  const userId = useUserStore(state => state.userId);
  const addNotification = useUIStore(state => state.addNotification);

  const strategies = useMemo(() => {
    if (!strategiesMap) return [];
    if (strategiesMap instanceof Map) return Array.from(strategiesMap.values());
    return Object.values(strategiesMap) as Strategy[];
  }, [strategiesMap]);

  const activeCount = useMemo(
    () => strategies.filter(s => s.status === 'active').length,
    [strategies]
  );

  // Modal state
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Strategy | null>(null);

  const handleCreate = useCallback(async (values: { name: string; description: string; timeHorizon: StrategyTimeHorizon }) => {
    const created = await createStrategy({ name: values.name, description: values.description || undefined, timeHorizon: values.timeHorizon, ownerId: userId });
    if (!created.ok || !created.id) {
      // Leave the modal open with what was typed so the user can retry.
      addNotification({
        type: 'error',
        title: 'Strategy was not created',
        message: created.error ?? 'Try again.',
        duration: 6000,
      });
      return;
    }
    setShowCreate(false);
    navigate(`/prioritization/strategy/${created.id}`);
  }, [createStrategy, navigate, userId, addNotification]);

  const handleSaveEdit = useCallback((values: { name: string; description: string; timeHorizon: StrategyTimeHorizon }) => {
    if (!editTarget) return;
    updateStrategy(editTarget.id, { name: values.name, description: values.description || undefined, timeHorizon: values.timeHorizon });
    setEditTarget(null);
  }, [editTarget, updateStrategy]);

  const handleDelete = useCallback(
    async (s: Strategy) => {
      if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
      const { ok, error } = await deleteStrategy(s.id);
      if (!ok && error) {
        window.alert(`Could not delete strategy: ${error}`);
      }
    },
    [deleteStrategy]
  );

  const tableColumns = useMemo((): TableColumnWithRender<Strategy>[] => [
    {
      key: 'name',
      label: 'Strategy',
      sortable: true,
      minWidth: 280,
      render: (strategy) => (
        <div className="min-w-0">
          <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate block text-sm">
            {strategy.name}
          </span>
          {strategy.description && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] truncate mt-0.5">
              {strategy.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      minWidth: 100,
      render: (strategy) => <StrategyStatusBadge status={strategy.status} />,
    },
    {
      key: 'timeHorizon',
      label: 'Horizon',
      sortable: true,
      minWidth: 110,
      render: (strategy) => (
        <span className="inline-flex items-center rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">
          {getTimeHorizonLabel(strategy.timeHorizon)}
        </span>
      ),
    },
    {
      key: 'drivers',
      label: 'Drivers',
      align: 'right',
      minWidth: 72,
      render: (strategy) => (
        <span className="tabular-nums text-xs text-[var(--text-ter)]">{strategy.strategicDrivers?.length ?? 0}</span>
      ),
    },
    {
      key: 'cycles',
      label: 'Cycles',
      align: 'right',
      minWidth: 72,
      render: (strategy) => (
        <span className="tabular-nums text-xs text-[var(--text-ter)]">{strategy.linkedCycleIds?.length ?? 0}</span>
      ),
    },
  ], []);

  return (
    <>
      <EntityListPage<Strategy, string>
        items={strategies}
        getId={(s) => s.id}
        masterDetail={false}
        title="Strategies"
        subtitle={`${strategies.length} strateg${strategies.length !== 1 ? 'ies' : 'y'} · ${activeCount} active`}
        icon={<ChartBarIcon className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBgClassName="bg-[var(--slate-dim)]"
        itemLabel="strategy"
        searchFields={['name', 'northStarGoal', 'description']}
        searchPlaceholder="Search strategies…"
        sortOptions={SORT_OPTIONS}
        defaultSort={{ field: 'updatedAt', direction: 'desc' }}
        facets={FACETS}
        tableColumns={tableColumns}
        viewModes={['table']}
        defaultViewMode="table"
        selectable={false}
        onItemClick={(s) => navigate(`/prioritization/strategy/${s.id}`)}
        renderRowActions={(strategy) => (
          <RowActions
            strategy={strategy}
            onEdit={setEditTarget}
            onDelete={handleDelete}
            onActivate={(s) => activateStrategy(s.id)}
            onArchive={(s) => archiveStrategy(s.id)}
          />
        )}
        emptyTitle="No strategies yet"
        emptyDescription="Create your first strategy to align prioritization with your organization's goals."
        emptyAction={{ label: 'New Strategy', onClick: () => setShowCreate(true) }}
        onCreate={() => setShowCreate(true)}
        createLabel="New Strategy"
        headerActions={<StrategyConnectivityHelpButton context="strategy_list" />}
        shellClassName="h-[calc(100vh-6rem)]"
      />

      {showCreate && (
        <StrategyFormModal
          mode="create"
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}

      {editTarget && (
        <StrategyFormModal
          mode="edit"
          initial={{
            name: editTarget.name,
            description: editTarget.description ?? '',
            timeHorizon: editTarget.timeHorizon,
          }}
          onSave={handleSaveEdit}
          onClose={() => setEditTarget(null)}
        />
      )}
    </>
  );
}

export default StrategyList;
